import { z } from 'zod';
import { UiLocale } from '../uiLocale.js';
import { isEmailAddress } from './email.js';
/**
 * Callable contracts for account emails (doc 88 D5).
 *
 * Optional fields are `.nullish()`, not `.optional()`: the callable SDK sends
 * an `undefined` property as `null`, which `.optional()` rejects as INTERNAL.
 */
/**
 * The password rule shown in the portal (doc 88 D11). Enforced in the UI only —
 * Firebase's own minimum (6) is the only server-side check until the Identity
 * Platform password policy is turned on — but it is the rule we tell people.
 */
export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 128;
export const PasswordSchema = z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH);
/** An address as the forms send it: trimmed and lower-cased before anything
 *  compares or hashes it, so `Owner@Example.com ` and `owner@example.com` are
 *  one address to the throttle and to the account lookup. */
export const AccountEmail = z
    .string()
    .trim()
    .toLowerCase()
    .max(254)
    .refine(isEmailAddress, { message: 'Not an email address' });
/**
 * `sendVerificationEmail` — signed in. Carries no address: the address is the
 * one in the caller's ID token, never one the client names (A01).
 */
export const SendVerificationEmailRequest = z.object({
    /** The portal locale the email is written in. Null → English. */
    language: UiLocale.nullish(),
});
export const SendVerificationEmailStatus = z.enum([
    /** Handed to the mail server. */
    'sent',
    /** Nothing to do — the address is already confirmed; refresh the token. */
    'already_verified',
    /** One was sent moments ago; `retryAfterSec` says when another may go. */
    'cooldown',
    /** Not sent: today's limit, or the mail service is not available. */
    'unavailable',
]);
export const SendVerificationEmailResponse = z.object({
    status: SendVerificationEmailStatus,
    retryAfterSec: z.number().int().nonnegative().nullable(),
});
/**
 * `requestPasswordReset` — no sign-in. The answer is ALWAYS `{ ok: true }`,
 * whether or not the address has an account (doc 88 D5).
 */
export const RequestPasswordResetRequest = z.object({
    email: AccountEmail,
    language: UiLocale.nullish(),
});
export const RequestPasswordResetResponse = z.object({ ok: z.literal(true) });
//# sourceMappingURL=auth.js.map